import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ContactEmailService } from '../../services/contact.service';
import { TranslationService } from '../../services/translation.service';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-footer',
  standalone: true,
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  imports: [ButtonComponent, ReactiveFormsModule],
})
export class FooterComponent implements OnInit, OnDestroy {
  translate = inject(TranslationService);
  emailService = inject(ContactEmailService);

  currentYear!: number;

  form = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2)],
    }),
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    message: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(10)],
    }),
  });

  readonly status = this.emailService.status;
  readonly errorMessage = this.emailService.errorMessage;

  readonly nameValue = signal('');
  readonly emailValue = signal('');
  readonly messageValue = signal('');

  readonly isSubmitting = computed(() => this.status() === 'sending');
  readonly isSuccess = computed(() => this.status() === 'success');
  readonly hasError = computed(() => this.status() === 'error');

  // Controla a animação de saída antes de remover o snackbar do DOM
  readonly isLeaving = signal(false);

  ngOnInit(): void {
    this.currentYear = new Date().getFullYear();

    this.form.controls.name.valueChanges.subscribe((v) =>
      this.nameValue.set(v),
    );
    this.form.controls.email.valueChanges.subscribe((v) =>
      this.emailValue.set(v),
    );
    this.form.controls.message.valueChanges.subscribe((v) =>
      this.messageValue.set(v),
    );
  }

  ngOnDestroy(): void {
    this.emailService.reset();
  }

  isFieldInvalid(field: 'name' | 'email' | 'message'): boolean {
    const control = this.form.controls[field];
    return control.invalid && (control.dirty || control.touched);
  }

  private dismissSnackbar(): void {
    // 1. Adiciona a classe de saída (anima para baixo)
    this.isLeaving.set(true);
    // 2. Só remove do DOM após a animação terminar (400ms)
    setTimeout(() => {
      this.emailService.reset();
      this.isLeaving.set(false);
    }, 400);
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { name, email, message } = this.form.getRawValue();

    await this.emailService.sendViaEmailJS({ name, email, message });

    if (this.isSuccess()) {
      this.form.reset();
    }

    // Um único timeout — dispara em ambos os casos (sucesso ou erro)
    setTimeout(() => this.dismissSnackbar(), 4000);
  }
}
